import type { ComponentProps } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '../../common/functions/cn';
import { Button, type ButtonProps } from '../button/button';
import OdinIcon from '../odin-icon/odin-icon.component';
import { dialogBaseStyles } from './dialog.component';

export interface FullScreenDialogProps extends Omit<ComponentProps<'dialog'>, 'open'> {
  /** Insets the panel from the viewport edges instead of filling it. */
  inset?: boolean;
  /** Names the dialog for assistive technology. A modal with no name is announced as nothing. */
  label: string;
}

export const FullScreenDialog = ({
  children,
  className,
  inset = false,
  label,
  onClose,
  ...props
}: FullScreenDialogProps) =>
  createPortal(
    <dialog
      data-slot="full-screen-dialog"
      aria-label={label}
      className={cn(
        'm-0 h-auto max-h-none w-auto max-w-none flex-col open:flex',
        'border-0 bg-background p-0 text-foreground outline-hidden',
        inset ? 'inset-4 rounded-sm' : 'inset-0 rounded-none',
        dialogBaseStyles,
        className
      )}
      onClose={(event) => {
        event.stopPropagation();
        onClose?.(event);
      }}
      {...props}
    >
      {children}
    </dialog>,
    document.body
  );

export const FullScreenDialogHeader = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    data-slot="full-screen-dialog-header"
    className={cn('flex shrink-0 items-center justify-between gap-2 p-3', className)}
    {...props}
  />
);

export const FullScreenDialogBody = ({ className, ...props }: ComponentProps<'div'>) => (
  <div data-slot="full-screen-dialog-body" className={cn('relative flex min-h-0 flex-1', className)} {...props} />
);

export const FullScreenDialogClose = ({ onClick, ...props }: ButtonProps) => {
  const { t } = useTranslation();

  return (
    <Button
      data-slot="full-screen-dialog-close"
      variant="ghost"
      size="icon"
      onClick={(event) => {
        onClick?.(event);
        event.currentTarget.closest('dialog')?.close();
      }}
      {...props}
    >
      <OdinIcon icon="X" />
      <span className="sr-only">{t('close')}</span>
    </Button>
  );
};
